/**
 * Delivery Boundary Validation Utility
 * Validates if a delivery address is within the hub's delivery boundary
 * Isolated from backend logic in controller/order.js
 */

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {Object} point1 - { latitude: number, longitude: number }
 * @param {Object} point2 - { latitude: number, longitude: number }
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (point1, point2) => {
  if (
    !point1 ||
    !point2 ||
    !point1.latitude ||
    !point1.longitude ||
    !point2.latitude ||
    !point2.longitude
  ) {
    return null;
  }

  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(point2.latitude - point1.latitude);
  const dLon = toRadians(point2.longitude - point1.longitude);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.latitude)) *
      Math.cos(toRadians(point2.latitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
};

/**
 * Convert degrees to radians
 */
const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Validate if delivery address is within hub's or restaurant's delivery boundary
 * @param {Object} source - Hub or Restaurant object with position coordinates
 * @param {Object} deliveryAddress - Delivery address with lat/lng
 * @param {number} maxDeliveryDistance - Maximum allowed distance in km (default: 30)
 * @returns {Object} Validation result with isValid, distanceInKm, and error message
 */
export const validateDeliveryBoundary = (
  source,
  deliveryAddress,
  maxDeliveryDistance = 30
) => {
  const isRestaurant = source?.restaurant === true;
  const sourceType = isRestaurant ? "Restaurant" : "Hub";

  // Validate inputs
  if (!source) {
    return {
      isValid: false,
      distanceInKm: null,
      error: `${sourceType} information is missing`
    };
  }

  if (!deliveryAddress) {
    return {
      isValid: false,
      distanceInKm: null,
      error: "Delivery address is missing"
    };
  }

  // Extract source coordinates
  let sourceLat, sourceLng;
  if (source.position) {
    // GeoJSON format: [longitude, latitude]
    if (Array.isArray(source.position.coordinates)) {
      sourceLng = source.position.coordinates[0];
      sourceLat = source.position.coordinates[1];
    } else if (source.position.lat && source.position.lng) {
      sourceLat = source.position.lat;
      sourceLng = source.position.lng;
    }
  } else if (source.lat && source.lng) {
    sourceLat = source.lat;
    sourceLng = source.lng;
  }

  // Handle case where coordinates might be 0 but valid (though unlikely for real addresses)
  if (
    sourceLat === undefined ||
    sourceLat === null ||
    sourceLng === undefined ||
    sourceLng === null
  ) {
    return {
      isValid: false,
      distanceInKm: null,
      error: `${sourceType} coordinates are missing or invalid`
    };
  }

  // Extract delivery address coordinates
  let deliveryLat, deliveryLng;
  if (deliveryAddress.position) {
    if (deliveryAddress.position.lat && deliveryAddress.position.lng) {
      deliveryLat = deliveryAddress.position.lat;
      deliveryLng = deliveryAddress.position.lng;
    } else if (Array.isArray(deliveryAddress.position.coordinates)) {
      deliveryLng = deliveryAddress.position.coordinates[0];
      deliveryLat = deliveryAddress.position.coordinates[1];
    }
  } else if (deliveryAddress.lat && deliveryAddress.lng) {
    deliveryLat = deliveryAddress.lat;
    deliveryLng = deliveryAddress.lng;
  }

  if (
    deliveryLat === undefined ||
    deliveryLat === null ||
    deliveryLng === undefined ||
    deliveryLng === null
  ) {
    return {
      isValid: false,
      distanceInKm: null,
      error: "Delivery address coordinates are missing or invalid"
    };
  }

  // Calculate distance
  const distanceInKm = calculateDistance(
    { latitude: sourceLat, longitude: sourceLng },
    { latitude: deliveryLat, longitude: deliveryLng }
  );

  if (distanceInKm === null) {
    return {
      isValid: false,
      distanceInKm: null,
      error: "Could not calculate distance"
    };
  }

  // Check if within boundary
  const isValid = distanceInKm <= maxDeliveryDistance;

  return {
    isValid,
    distanceInKm: parseFloat(distanceInKm.toFixed(2)),
    maxDeliveryDistance,
    error: isValid
      ? null
      : `Delivery address is too far from the ${sourceType.toLowerCase()}. Maximum delivery distance is ${maxDeliveryDistance} km, but the distance is ${distanceInKm.toFixed(2)} km. Please select a delivery address within the ${sourceType.toLowerCase()}'s coverage area.`,
    source: {
      name: source.name || `${source.firstname} ${source.lastname}`,
      city: source.city,
      type: sourceType
    }
  };
};
